import React, { FC, MouseEvent, useCallback, memo } from 'react';
import styled, { css } from 'styled-components';
import Checkbox from '@minecraft.minusable-checkbox';
import UncontrolledArrow from '@minecraft.uncontrolled-arrow';
import { PillProps, SelectOption } from '@minecraft.typings';
import { helpers } from '@minecraft.utils';
import { DropdownWithChildrenProps } from '../shared/types';
import { DesignSystemBgColor, DesignSystemTextColor } from '../../themes';
import { DetailText, FlexCol } from '../../../atoms';

const { getAllNestedOptions } = helpers;

const PILL_VARIANTS = {
  primary: 'primary',
  disabled: 'disabled',
} as const;

type PillVariants = keyof typeof PILL_VARIANTS;

type PillColors = {
  bgColor: DesignSystemBgColor;
  labelColor: DesignSystemTextColor;
};

const PILL_COLOR_MAP: Record<PillVariants, PillColors> = {
  primary: {
    bgColor: 'primary',
    labelColor: 'inverted',
  },
  disabled: {
    bgColor: 'pillDisabled',
    labelColor: 'disabled',
  },
};

const Pill = ({ label, color }: PillProps) => {
  const { bgColor, labelColor } = PILL_COLOR_MAP[color];

  return (
    <FlexCol
      data-testid="dropdown-item-pill"
      grow={0}
      bgColor={bgColor}
      borderRadius="full"
      className="cn_atom_px-2 cn_atom_py-1"
    >
      <DetailText color={labelColor} lineHeight="expanded">
        {label}
      </DetailText>
    </FlexCol>
  );
};

export const StyledDropdownItem = styled.div<{
  $isItemSelected?: boolean;
  $isItemDisabled?: boolean;
  $isMultipleSelect?: boolean;
  $nestingLevel: number;
}>`
  background-color: ${(props) =>
    props.$isItemSelected ? props.theme.form.option.state.selected.backgroundColor : 'transparent'};
  border: 0;
  font-size: 0.875rem;
  text-align: inherit;
  width: 100%;

  ${(props) =>
    !props.$isItemDisabled &&
    css`
      cursor: ${props.$isMultipleSelect ? 'default' : 'pointer'};
    `}

  ${(props) =>
    props.$nestingLevel === 1 &&
    `
    font-weight: 600;
  `}
  ${(props) =>
    props.$nestingLevel === 2 &&
    `
    font-weight: 500;
  `}
`;

const StyledDropdownItemInner = styled.div`
  display: flex;
  padding: 0.5rem;
  justify-content: space-between;
`;

const StyledDropdownItemInnerLabel = styled.span`
  white-space: pre-wrap;
`;

const StyledCheckbox = styled(Checkbox)`
  margin: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
`;

const StyledUncontrolledArrow = styled(UncontrolledArrow)`
  margin-left: 1rem;
  height: 1rem;
  width: 1rem;
`;

interface Props extends Partial<DropdownWithChildrenProps> {
  isItemSelected: boolean;
  isItemDisabled: boolean;
  isItemCountExist: boolean;
  areSubOptionsExist: boolean;
  checkboxEnable: boolean;
  minusEnable: boolean;
  nestingLevel: number;
  item: SelectOption;
  toggleParentOptions: (parentItem: SelectOption) => void;
  onDropdownItemSelect: (newItems: SelectOption[]) => void;
  className?: string;
}

const DropdownWithChildrenDropdownItem: FC<Props> = ({
  isMultipleSelect,
  isCollapsable,
  isItemSelected,
  isItemDisabled,
  isItemCountExist,
  areSubOptionsExist,
  checkboxEnable,
  nestingLevel,
  minusEnable,
  item,
  toggleParentOptions,
  onDropdownItemSelect,
  className,
  dataTestId,
}) => {
  const handleItemClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (isItemDisabled || (isMultipleSelect && !checkboxEnable) || (!isMultipleSelect && areSubOptionsExist)) {
        return;
      }

      const getNewItems = () => {
        if (!isMultipleSelect && !areSubOptionsExist) {
          return [item];
        }

        return areSubOptionsExist ? getAllNestedOptions(item.options) : [item];
      };

      event.preventDefault();

      onDropdownItemSelect(getNewItems());
    },
    [isMultipleSelect, isItemDisabled, areSubOptionsExist, checkboxEnable, item, onDropdownItemSelect]
  );

  const handleArrowClick = useCallback(() => {
    toggleParentOptions(item);
  }, [item, toggleParentOptions]);

  const renderLabel = useCallback((): JSX.Element => {
    return (
      <StyledDropdownItemInnerLabel data-testid={`option-${item.label}`} data-qa-id={`option-${item.value}`}>
        {item.label}
        {isItemCountExist ? ` (${item.count})` : ''}
      </StyledDropdownItemInnerLabel>
    );
  }, [isItemCountExist, item]);

  return (
    <StyledDropdownItem
      role="menuitem"
      $isMultipleSelect={isMultipleSelect && checkboxEnable}
      $isItemSelected={
        (isMultipleSelect && isItemSelected && checkboxEnable) ||
        (!isMultipleSelect && isItemSelected && !areSubOptionsExist)
      }
      $isItemDisabled={isItemDisabled}
      $nestingLevel={item?.options && nestingLevel}
      onClick={handleItemClick}
      className={className}
      data-qa-id={!item?.options ? 'dropdown-sub-item' : 'dropdown-title-item'}
      data-testid="dropdown-item-wrapper"
      aria-label={item['aria-label']}
    >
      <StyledDropdownItemInner
        className="dropdown-item-inner"
        data-qa-id={`${item.value?.toString().toLocaleLowerCase()}-option`}
      >
        {isMultipleSelect && checkboxEnable ? (
          <StyledCheckbox
            isChecked={isItemSelected}
            minusEnable={minusEnable}
            disabled={isItemDisabled}
            data-qa-id={item.value?.toString()}
            data-testid={`${dataTestId}-item-${item.value}`}
            readOnly
            onChange={() => undefined}
          >
            {renderLabel()}
          </StyledCheckbox>
        ) : (
          renderLabel()
        )}
        {item.pill && <Pill label={item.pill.label} color={item.pill.color || 'primary'} />}
        {isCollapsable && areSubOptionsExist && nestingLevel === 1 && (
          <StyledUncontrolledArrow onClick={handleArrowClick} />
        )}
      </StyledDropdownItemInner>
    </StyledDropdownItem>
  );
};

export default memo(DropdownWithChildrenDropdownItem);
